import React, { useState } from 'react';
import { 
  PencilIcon, 
  TrashIcon, 
  EyeIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { parseDate } from '../utils/dateUtils';
import useCourseUsers from '../hooks/useCourseUsers';

const DocumentTable = ({ 
  documents, 
  loading, 
  onEdit, 
  onDelete, 
  onView, 
  onViewSubcollection,
  collectionName,
  hasSubcollections = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Fetch users for displaying user information
  const { getUserById } = useCourseUsers();

  const filteredDocuments = documents.filter(doc => {
    const searchString = searchTerm.toLowerCase();
    return Object.entries(doc).some(([key, value]) => {
      // Skip delivery field from search
      if (key === 'delivery') return false;
      
      if (typeof value === 'string') {
        return value.toLowerCase().includes(searchString);
      }
      if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value).toLowerCase().includes(searchString);
      }
      return false;
    });
  });

  // Function to extract user information from document
  const getUserInfo = (doc) => {
    // Check for common user ID fields
    const userId = doc.userId || doc.user_id || doc.author_id || doc.authorId || doc.createdBy || doc.senderId;
    
    if (userId) {
      const user = getUserById(userId);
      if (user) {
        return {
          id: user.id,
          name: user.name || 'Unknown',
          email: user.email || '',
          role: user.role || 'User'
        };
      }
    }
    
    // Check for embedded user objects
    if (doc.author && typeof doc.author === 'object') {
      return {
        id: doc.author.id || 'Unknown',
        name: doc.author.name || 'Unknown',
        email: doc.author.email || '',
        role: doc.author.role || 'Author'
      };
    }
    
    if (doc.sender && typeof doc.sender === 'object') {
      return {
        id: doc.sender.id || 'Unknown',
        name: doc.sender.name || 'Unknown',
        email: doc.sender.email || '',
        role: doc.sender.role || 'Sender'
      };
    }
    
    return null;
  };

  const sortedDocuments = [...filteredDocuments].sort((a, b) => {
    if (!sortField) return 0;
    
    let aVal = a[sortField];
    let bVal = b[sortField];
    
    // Handle different data types
    if (typeof aVal === 'object' && aVal !== null) {
      aVal = JSON.stringify(aVal);
    }
    if (typeof bVal === 'object' && bVal !== null) {
      bVal = JSON.stringify(bVal);
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatValue = (value, key) => {
    if (value === null || value === undefined) return (
      <span className="text-gray-400 italic">-</span>
    );
    
    if (typeof value === 'object') {
      if (value._seconds) {
        // Firestore timestamp - use improved date parsing
        const date = parseDate(value);
        
        if (!date) {
          return (
            <span className="text-sm text-red-500">Invalid date</span>
          );
        }
        
        return (
          <span className="text-sm">
            <div className="text-gray-900">{date.toLocaleDateString()}</div>
            <div className="text-gray-500">{date.toLocaleTimeString()}</div>
          </span>
        );
      }
      if (Array.isArray(value)) {
        return (
          <div className="max-w-xs text-sm text-gray-900">
            {value.map((item, index) => (
              <span key={index}>
                {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                {index < value.length - 1 && ', '}
              </span>
            ))}
          </div>
        );
      }
      // For regular objects, show a clean JSON representation
      return (
        <div className="max-w-xs text-sm text-gray-900 font-mono">
          <pre className="whitespace-pre-wrap break-words">
            {JSON.stringify(value, null, 2)}
          </pre>
        </div>
      );
    }
    
    if (typeof value === 'boolean') {
      return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Yes' : 'No'}
        </span>
      );
    }
    
    if (typeof value === 'number') {
      // Format currency if it looks like a price
      if (key.includes('price') || key.includes('amount') || key === 'pricee') {
        return (
          <span className="font-medium text-green-600">
            ${value.toLocaleString()}
          </span>
        );
      }
      // Format ratings
      if (key.includes('rating')) {
        return (
          <div className="flex items-center">
            <span className="text-yellow-400">{'★'.repeat(Math.floor(value))}</span>
            <span className="ml-1 text-sm text-gray-600">({value})</span>
          </div>
        );
      }
      return (
        <span className="font-mono text-sm">{value.toLocaleString()}</span>
      );
    }
    
    // Handle URLs
    if (typeof value === 'string' && (value.startsWith('http') || value.startsWith('www'))) {
      return (
        <a 
          href={value.startsWith('http') ? value : `https://${value}`}
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline truncate block max-w-xs"
        >
          {value.length > 30 ? value.substring(0, 30) + '...' : value}
        </a>
      );
    }
    
    // Handle emails
    if (typeof value === 'string' && value.includes('@') && value.includes('.')) {
      return (
        <a 
          href={`mailto:${value}`}
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {value}
        </a>
      );
    }
    
    if (typeof value === 'string' && value.length > 50) {
      return (
        <span className="group relative">
          <span className="truncate block max-w-xs">{value.substring(0, 50)}...</span>
          <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 max-w-xs break-words">
            {value}
          </div>
        </span>
      );
    }
    
    return String(value);
  };

  const getTableHeaders = () => {
    if (documents.length === 0) return [];
    
    const allKeys = new Set();
    documents.forEach(doc => {
      Object.keys(doc).forEach(key => {
        if (key !== 'id' && key !== 'delivery') allKeys.add(key);
      });
    });
    
    const keysArray = Array.from(allKeys);
    
    // Prioritize important fields based on collection type
    const priorityFields = {
      'users': ['name', 'email', 'role', 'createdAt'],
      'courses': ['name', 'pricee', 'price_status', 'image_url'],
      'books': ['title', 'amount', 'description'],
      'reviews': ['rating', 'review', 'course_id'],
      'classrooms': ['name', 'subject', 'category', 'memberCount'],
      'notifications': ['title', 'description', 'topic'],
      'withdrawals': ['amount', 'status', 'method', 'userId']
    };
    
    const priority = priorityFields[collectionName] || [];
    const prioritized = priority.filter(key => keysArray.includes(key));
    const remaining = keysArray.filter(key => !priority.includes(key));
    
    // Limit to 5 columns to make room for Users column
    return [...prioritized, ...remaining].slice(0, 5);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-xl overflow-hidden rounded-xl border border-gray-200">
      {/* Search bar */}
      <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm"
          />
        </div>
      </div>

      {sortedDocuments.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No documents found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      ID
                    </span>
                  </div>
                </th>
                {getTableHeaders().map(header => (
                  <th
                    key={header}
                    onClick={() => handleSort(header)}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center">
                      {header.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')}
                      {sortField === header && (
                        <span className="ml-1">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center">
                    <UserIcon className="h-4 w-4 mr-2 text-gray-400" />
                    Users
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedDocuments.map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                      {doc.id.length > 10 ? doc.id.substring(0, 10) + '...' : doc.id}
                    </span>
                  </td>
                  {getTableHeaders().map(header => (
                    <td key={header} className="px-6 py-4 text-sm text-gray-500">
                      {formatValue(doc[header], header)}
                    </td>
                  ))}
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {getUserInfo(doc) ? (
                      <div className="flex items-center">
                        <UserIcon className="h-4 w-4 mr-1 text-gray-500" />
                        <span className="text-sm text-gray-900">
                          {getUserInfo(doc).name}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400 italic">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => onView(doc)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onEdit(doc)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onDelete(doc.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                      {hasSubcollections && (
                        <button
                          onClick={() => onViewSubcollection(doc)}
                          className="text-green-600 hover:text-green-900"
                          title="View Subcollections"
                        >
                          <ChevronRightIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DocumentTable;
